/**
 * DualCTAVideoLanding — fullscreen membership funnel (dual + variants + Mux)
 *
 * Rutas: AR `/ser-miembro` · intl `/membership` · home no-AR vía USExclusiveLanding.
 * Legacy `/video-landing` redirige vía worker + MembershipLandingRoute.
 *
 * Full-screen autoplay Mux + CTA panel:
 *   VIP  → Stripe (contrato Exclusive Membership + price/promo de variante o fallback)
 *   Free → OFF por defecto (opt-in admin: visibility + dual_cta_free_enabled + free_enabled)
 *
 * Layout:
 *   Mobile  → video full-screen + CTA overlay at bottom (sin badges/trust chips)
 *   Desktop → video 9:16 card │ copy + benefits + stacked CTAs
 *
 * Checkout VIP (todos los países, incl. AR):
 *   resolveVipStripeCheckoutFromFallback(variant, intlFallback)
 *   → startVipMembershipVideoCheckout (Stripe session, success → /portal/complete-profile)
 *
 * País: window.__TEMPLATE_CONFIG__.country (Cloudflare Worker).
 */

import MuxVideoPlayerWithCTA from "@/components/MuxVideoPlayerWithCTA";
import MuxPosterBackdrop from "@/components/MuxPosterBackdrop";
import { CheckoutRedirectOverlay } from "@/components/CheckoutRedirectOverlay";
import { MembershipVideoCtaPanel } from "@/components/MembershipVideoCtaPanel";
import { MembershipFunnelDesktopAside } from "@/components/MembershipFunnelConversionChrome";
import { useMembershipCtaEngagement } from "@/hooks/useMembershipCtaEngagement";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchPremiumInvitePublicConfig } from "@/lib/premiumInvitePublicConfig";
import {
  applyInstagramBrowserCSS,
  isInstagramBrowser,
} from "@/lib/instagramBrowserDetection";
import { cn } from "@/lib/utils";
import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { detectCountryCode } from "@/lib/countryDetection";
import { useDualCtaVariant } from "@/hooks/useDualCtaVariant";
import { DualCtaLiveShell } from "@/components/DualCtaLiveShell";
import { USExclusiveLandingSeoFooter } from "@/components/USExclusiveLandingSeoFooter";
import { resolveWorkerLanguageBase } from "@/lib/visitorExperienceExpectation";
import { detectTrafficSource } from "@/lib/detectTrafficSource";
import {
  getSlackFunnelDiagnosticsExtraPostHog,
  trackDualCtaVideoLandingPostHog,
  trackPageViewPostHog,
  trackVideoPlayPostHog,
  trackVideoCompletePostHog,
  registerDualCtaVideoLandingContextPostHog,
  registerDualCtaVariantContextPostHog,
  type DualCtaVideoLandingAction,
} from "@/lib/posthogAnalytics";
import { startVipMembershipVideoCheckout } from "@/lib/vipMembershipVideoCheckout";
import { CheckoutEmailCaptureModal } from "@/components/CheckoutEmailCaptureModal";
import { resolvePreCheckoutEmail } from "@/lib/preCheckoutEmail";
import { useDualCtaLabels } from "@/lib/useDualCtaLabels";
import DualCtaPresentationExtras from "@/components/DualCtaPresentationExtras";
import { useDualVariantTitle } from "@/lib/useDualVariantTitle";
import { getServerTemplateConfig, type DualVariantConfig } from "@/lib/serverTemplateDetection";
import { useDualCtaVisibility } from "@/hooks/useDualCtaVisibility";
import {
  trackDualEvent,
  trackInitiateCheckoutDual,
  trackLeadDual,
  trackVideoViewStartedDual,
  trackVideoViewCompletedDual,
  trackAddToCartDual,
} from "@/lib/metaCapi";
import { heroConversionGate } from "@/lib/heroConversionGate";
import { hasMembershipTelegram } from "../../shared/membershipClerkMetadata";
import { buildMembershipFunnelQuery } from "@/lib/membershipFunnelEnglish";
import {
  shouldRedirectArMembershipToHome,
} from "@/lib/arIntlSeoFunnelRedirect";
import { getMembershipLandingPath } from "@/lib/membershipLandingPath";
import {
  dualCtaLandingText,
} from "@/lib/dualCtaLandingStrings";
import { TELEGRAM_GROUP_URL } from "@/config/telegramGroups";
import { isPremiumSpecialOfferActive } from "@/config/bioTemplatePremiumPlans";
import { BRAND_NAME, BRAND_PERSON_NAME } from "@/lib/brandConfig";
import { useAdminEmbeddedPreviewDocument } from "@/hooks/useAdminEmbeddedPreviewDocument";
import {
  MUX_FULLSCREEN_FRAME_CLASS,
  MUX_FULLSCREEN_SHELL_CLASS,
} from "@/lib/adminLivePreview";
import { useUser, useAuth } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { useIsSuperAdmin } from "@/lib/auth";
import { TemplateLoadingScreen } from "@/components/templates/TemplateLoadingScreen";
import { VpnBlockedScreen } from "@/components/templates/VpnBlockedScreen";

import { resolvePremiumHeroPlaybackId } from "@/lib/muxBrandPlayback";
import {
  resolveIntlDualCtaFallbackForVisitor,
  resolveVipStripeCheckoutFromFallback,
} from "@/lib/dualCtaIntlFallback";
import { launchIntlExclusiveMembershipCheckout } from "@/lib/intlExclusiveMembershipCheckout";
import { WORKER_DEFAULT_TEMPLATE } from "@/config/workerCountryTemplateMap";

// ── Build-time fallbacks ───────────────────────────────────────────────────────
const ENV_PLAYBACK_ID = resolvePremiumHeroPlaybackId();

const ENV_FREE_TELEGRAM_URL =
  (import.meta.env.VITE_PREMIUM_SIMPLE_FREE_TELEGRAM_URL as string | undefined)?.trim() ||
  TELEGRAM_GROUP_URL;

const VIP_DISCOUNT_RATE = (() => {
  const raw = String(import.meta.env.VITE_PREMIUM_VIP_DISCOUNT_RATE ?? "").trim();
  const n = raw ? parseFloat(raw) : NaN;
  return Number.isFinite(n) && n >= 0 ? n : 0.1;
})();

// ── i18n ───────────────────────────────────────────────────────────────────────

type Lang = "es" | "en";

const SPANISH_COUNTRIES_LANG = new Set([
  "AR","MX","CO","CL","PE","PY","ES","BO","EC","GT",
  "HN","NI","PA","DO","CU","CR","VE","UY","PR",
]);

/**
 * Base de idioma resuelta por el worker (es/en + pt/de/fr/it si el flag los
 * habilita). El worker ya aplicó normalizeSupportedLanguage, así que con el flag
 * off sólo llega es/en. Si no hay __TEMPLATE_CONFIG__ (SPA nav / preview),
 * cae al país (set ES) — el único camino que aún usa SPANISH_COUNTRIES_LANG.
 */
function serverLangBase(): string {
  const tag = getServerTemplateConfig()?.language || "";
  const base = tag.split("-")[0].toLowerCase();
  if (base) return base;
  const c = (detectCountryCode() || "").toUpperCase();
  return SPANISH_COUNTRIES_LANG.has(c) ? "es" : "en";
}

/**
 * Idioma para el chrome inline (I18N solo tiene es/en) y para el fallback de
 * visibility (useDualCtaVisibility, es/en). Cualquier idioma extra (pt/de/fr/it)
 * cae a 'en' acá — las labels del CTA igual se localizan vía el snapshot del
 * worker (pickLangField) y el fallback REST useDualCtaLabels(labelLang).
 * Respeta el idioma del worker, no sólo el país: así un visitor US es-US
 * (diáspora latina) ahora obtiene chrome en español, no inglés.
 */
function detectUiLang(): Lang {
  return serverLangBase() === "es" ? "es" : "en";
}

// Los defaults de UI (chrome + labels sin override admin/variante) viven en el
// módulo canónico multilingüe `dualCtaLandingStrings` (es/en/pt/de/fr/it). El
// componente ya NO mantiene su propio mapa binario es/en — `localT` (scope del
// componente, más abajo) delega ahí usando `labelLang` (base real del worker).

// ── Country helpers ────────────────────────────────────────────────────────────

const AR_COUNTRIES = new Set(["AR"]);
const MIDDLE_EAST_COUNTRIES = new Set([
  "AE", "SA", "EG", "QA", "KW", "OM", "IL", "JO", "IQ", "LB", "TR", "MA", "TN", "DZ",
]);
const ASIA_COUNTRIES = new Set([
  "IN", "PK", "BD", "CN", "JP", "KR", "TW", "HK", "SG", "MY", "TH", "ID", "PH", "VN",
]);
const PRIORITY_STRIPE_DUAL_COUNTRIES = new Set(["IL", "JO", "IQ", "LB", "TR", "MA", "TN", "DZ"]);

/**
 * País del visitante para decidir el gateway (MP-first en AR).
 * Prioriza el config del Worker, pero cae al detector canónico
 * (`detectCountryCode`: timezone/idioma) cuando el Worker no inyectó el país —
 * así el argentino no termina en Stripe por un "UNKNOWN".
 */
function getVisitorCountry(): string {
  const fromWorker = (getServerTemplateConfig()?.country ?? "").toUpperCase();
  // "UNKNOWN" is the Railway-served placeholder — only treat the worker hint
  // as authoritative when it's a real ISO2 (not "UNKNOWN").
  if (/^[A-Z]{2}$/.test(fromWorker) && fromWorker !== "UNKNOWN") return fromWorker;
  return (detectCountryCode() ?? "UNKNOWN").toUpperCase();
}

function resolveCountryTemplateRegion(countryCode: string): "Medio Oriente" | "Asia" | "Other" {
  if (MIDDLE_EAST_COUNTRIES.has(countryCode)) return "Medio Oriente";
  if (ASIA_COUNTRIES.has(countryCode)) return "Asia";
  return "Other";
}

// ── Attribution + session-context helpers ──────────────────────────────────────

function extractUtmParams(): Record<string, string> {
  try {
    const p = new URLSearchParams(window.location.search);
    const out: Record<string, string> = {};
    for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
      const v = p.get(k);
      if (v) out[k] = v.slice(0, 200);
    }
    return out;
  } catch { return {}; }
}

function detectDeviceClass(): "mobile" | "tablet" | "desktop" {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return "tablet";
  if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua)) return "mobile";
  return "desktop";
}

function detectOSPlatform(): string {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  if (/android/i.test(ua)) return "android";
  if (/windows/i.test(ua)) return "windows";
  if (/mac os x/i.test(ua)) return "macos";
  if (/linux/i.test(ua)) return "linux";
  return "other";
}

function getCountryConfidenceSource(): "worker" | "timezone_fallback" | "unknown" {
  const fromWorker = (getServerTemplateConfig()?.country ?? "").toUpperCase();
  if (/^[A-Z]{2}$/.test(fromWorker) && fromWorker !== "UNKNOWN") return "worker";
  return detectCountryCode() ? "timezone_fallback" : "unknown";
}

/** Returns true if the visitor has been to this funnel before (localStorage flag). */
function checkAndMarkReturningVisitor(): boolean {
  try {
    const k = "jp_dual_seen";
    const was = !!localStorage.getItem(k);
    localStorage.setItem(k, "1");
    return was;
  } catch { return false; }
}

// ── Component ──────────────────────────────────────────────────────────────────

/** PostHog + CurrentTemplateContext name for the dual funnel surface. */
export type DualCTAVideoLandingHostTemplate =
  | "DualCTAVideoLanding"
  | "USExclusiveLanding";

export interface DualCTAVideoLandingProps {
  /**
   * `USExclusiveLanding` = country-template default intl (`/admin/country-template`).
   * Worker locale + Stripe contrato `prod_UqbVqDInMZ1S48`.
   * `DualCTAVideoLanding` = override explícito admin (p. ej. `/membership` EN fijo).
   */
  hostTemplate?: DualCTAVideoLandingHostTemplate;
}

/** Default intl = USExclusiveLanding (worker map + country-template admin). */
export function resolveDualCtaHostTemplate(
  explicit?: DualCTAVideoLandingHostTemplate,
): DualCTAVideoLandingHostTemplate {
  if (explicit) return explicit;
  const tpl =
    getServerTemplateConfig()?.templateName?.trim() ||
    WORKER_DEFAULT_TEMPLATE.template;
  if (tpl === "DualCTAVideoLanding" || tpl === "BaseTemplateInternationalDual") {
    return "DualCTAVideoLanding";
  }
  return "USExclusiveLanding";
}

export default function DualCTAVideoLanding({
  hostTemplate: hostTemplateProp,
}: DualCTAVideoLandingProps = {}) {
  const hostTemplate = resolveDualCtaHostTemplate(hostTemplateProp);
  return (
    <DualCtaLiveShell>
      <DualCTAVideoLandingPage hostTemplate={hostTemplate} />
    </DualCtaLiveShell>
  );
}

function DualCTAVideoLandingPage({
  hostTemplate,
}: DualCTAVideoLandingProps) {
  const isUsExclusiveHost = hostTemplate === "USExclusiveLanding";
  const embeddedPreview = useAdminEmbeddedPreviewDocument();
  const { setTemplateName } = useCurrentTemplate();
  const isSuperAdmin = useIsSuperAdmin();

  const visitorCountry = getVisitorCountry();
  const isArVisitor = AR_COUNTRIES.has(visitorCountry);

  // AR visitors on /invitacion-premium → BaseTemplate home (legacy premium invite).
  // AR en /ser-miembro y /membership → BaseTemplate home SIEMPRE (funnel de
  // membership prohibido para Argentina — paridad worker + MembershipLandingRoute).
  const isInvitacionPremiumRoute =
    typeof window !== "undefined" &&
    window.location.pathname === "/invitacion-premium";
  const isPreviewVariant =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("_force_variant");
  const shouldForceBaseTemplate =
    isArVisitor &&
    !isSuperAdmin &&
    !embeddedPreview &&
    (isInvitacionPremiumRoute ||
      (typeof window !== "undefined" &&
        shouldRedirectArMembershipToHome({
          pathname: window.location.pathname,
          search: window.location.search,
          skip: isPreviewVariant,
        })));

  // Intl `/membership`: English chrome. USExclusive home (`/`): worker locale (de/fr/pt…).
  const labelLang = isArVisitor
    ? serverLangBase()
    : isUsExclusiveHost
      ? resolveWorkerLanguageBase(visitorCountry)
      : "en";
  const lang: Lang = isArVisitor
    ? detectUiLang()
    : isUsExclusiveHost
      ? labelLang === "es"
        ? "es"
        : "en"
      : "en";
  // Defaults de UI localizados (chrome + labels sin override) desde el módulo
  // canónico. Reemplaza el viejo mapa binario es/en; el 2º arg (uiLang) se ignora
  // — usamos labelLang (base real) para que pt/de/fr/it rinda en su idioma.
  const localT = (
    key: Parameters<typeof dualCtaLandingText>[0],
    _lang?: Lang,
  ): string => dualCtaLandingText(key, labelLang);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (!isUsExclusiveHost || isArVisitor) return;
    void i18n.changeLanguage(labelLang);
    document.documentElement.lang = labelLang;
  }, [isUsExclusiveHost, isArVisitor, labelLang, i18n]);
  const { isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const specialOfferActive = isPremiumSpecialOfferActive();

  // AR (non-super-admin) visitors must go through BaseTemplate on this route.
  // Redirect client-side (except super admins and admin embedded previews).
  useEffect(() => {
    if (shouldForceBaseTemplate) {
      // Track for understanding AR vs rest-of-world behavior in PostHog (funnels, conversion by template).
      if (typeof window !== "undefined" && (window as any).posthog) {
        (window as any).posthog.capture("ar_redirect_to_base_template", {
          reason: isInvitacionPremiumRoute
            ? "invitacion_premium"
            : "intl_seo_funnel_query",
          target: "BaseTemplate",
          pathname: window.location.pathname,
          search: window.location.search,
          timestamp: new Date().toISOString(),
        });
      }
      navigate("/", { replace: true });
    }
  }, [shouldForceBaseTemplate, navigate]);

  // (Antes había constantes `adminVipLabel` y `adminNonArUrl` hardcoded a
  // null como placeholders del legacy hero-CTA override — eliminadas porque
  // ensuciaban la cascada visible sin aportar nada. La cascada efectiva ahora
  // pasa por liveCTA.dualCtaVisibility (worker-driven) → variant → adminLabels.)

  // ── A/B variant (worker → __TEMPLATE_CONFIG__.dualVariant) ──────────────────
  // Label cascade (primera fuente no-nula gana):
  //   1. workerSurfaceVisibility.vipLabel/freeLabel
  //         → dual_cta_visibility admin (/admin/dual-cta-visibility), per-surface override
  //   2. variant?.vipLabel / variant?.freeLabel
  //         → dual_cta_variants admin (/admin/dual-cta-variants), per A/B variant
  //   3. apiVipLabel / apiFreeTelegramLabel
  //         → premium_invite_config table (/admin/premium-invite)
  //   4. adminLabels.paid / adminLabels.free
  //         → workerAdminLabels (KV key dual_cta_labels, written by variants admin for DEFAULT variant)
  //         → fallbackAdminLabels (api/dual-cta-labels: reads DEFAULT variant from dual_cta_variants,
  //                                falls back to stripe_checkout_config)
  //   5. localT("vipLabel"/"freeLabel", lang) — i18n hardcoded
  //
  // Steps 2 + 4 are both managed from /admin/dual-cta-variants (per-variant and DEFAULT variant labels).
  // Si el worker no inyectó variante (sin assignment en el geo, o KV vacío)
  // el flujo queda igual que antes — sin regresión.
  //
  // useDualCtaVariant centraliza la cascada (live > spa > worker seed) + SPA cookie fallback.
  // El hook también expone abBucket de forma estable.
  const { variant, abBucket, liveCTA } = useDualCtaVariant({ resolveLang: labelLang });

  // Fallback REST de la variante resuelta (api/dual-cta-labels — MISMA cascada
  // geo que el worker: city_ar > city > province_ar > state_us > country >
  // DEFAULT). Declarado ANTES de intlFallback/playbackId porque ambos lo leen;
  // antes se declaraba ~60 líneas después y el acceso lanzaba ReferenceError
  // (TDZ) en cold start sin variante con video. labelLang (no uiLang) para que
  // el fallback sirva pt/de/fr/it cuando el worker resolvió ese idioma.
  const fallbackAdminLabels = useDualCtaLabels(labelLang);

  const intlFallback = useMemo(
    () =>
      resolveIntlDualCtaFallbackForVisitor({
        variantMuxPlaybackId: variant?.muxPlaybackId,
        // Precio/Stripe price id: variante (worker/SPA) > fallback REST
        // geo-cascade (cold start / worker caído) > contrato hardcodeado. Sin
        // el 2do nivel, un visitor en cold start en una ciudad custom con precio
        // propio veía/pagaba el contrato genérico en vez del precio de su geo.
        variantPriceUsd: variant?.priceUsd ?? fallbackAdminLabels.priceUsd,
        variantStripePriceId: variant?.stripePriceId ?? fallbackAdminLabels.stripePriceId,
        variantVipLabelEn: variant?.vipLabel,
        variantVipLabelEs: variant?.vipLabel,
      }),
    [
      variant?.muxPlaybackId,
      variant?.priceUsd,
      variant?.stripePriceId,
      variant?.vipLabel,
      fallbackAdminLabels.priceUsd,
      fallbackAdminLabels.stripePriceId,
    ],
  );

  // Stable session-context values (read once at mount, never change during lifecycle)
  const utmParams      = useMemo(() => extractUtmParams(), []);
  const deviceClass    = useMemo(() => detectDeviceClass(), []);
  const deviceOS       = useMemo(() => detectOSPlatform(), []);
  const isReturnVisitor = useMemo(() => checkAndMarkReturningVisitor(), []);

  const variantRef = useRef<DualVariantConfig | undefined>(variant);
  useEffect(() => {
    variantRef.current = variant;
  }, [variant]);

  const [globalInviteFallback, setGlobalInviteFallback] = useState<{
    playbackId: string | null;
    freeTelegramUrl: string | null;
    freeEnabled: boolean | null;
  }>({ playbackId: null, freeTelegramUrl: null, freeEnabled: null });

  // Video: variante (worker/SPA) > fallback REST de la variante (cold start /
  // worker caído) > config global premium-invite > default intl > env.
  const playbackId =
    variant?.muxPlaybackId?.trim() ||
    fallbackAdminLabels.muxPlaybackId?.trim() ||
    globalInviteFallback.playbackId ||
    intlFallback.muxPlaybackId ||
    ENV_PLAYBACK_ID;
  // Free CTA: toggle **por variante** (admin dual-cta-variants).
  // Default OFF (null/undefined/false). Sin heredar global premium-invite.
  // Sin variante (worker caído / cold start), el fallback REST representa la
  // misma variante resuelta para este geo → respeta su free_enabled.
  const freeEnabledByVariant = variant
    ? variant.freeEnabled === true
    : fallbackAdminLabels.freeEnabled === true;
  // free URL solo cuando la variante lo prende (free-join / telegram post-aprobación).
  const freeTelegramUrl = freeEnabledByVariant
    ? variant?.freeTelegramUrl?.trim() ||
      fallbackAdminLabels.freeTelegramUrl?.trim() ||
      globalInviteFallback.freeTelegramUrl ||
      ENV_FREE_TELEGRAM_URL
    : undefined;

  // ── State ────────────────────────────────────────────────────────────────────
  const [apiVipLabel, setApiVipLabel]           = useState<string>("");
  const [apiFreeTelegramLabel, setApiFreeTelegramLabel] = useState<string>("");
  const [posterReady, setPosterReady]           = useState(false);
  const [videoNeedsGesture, setVideoNeedsGesture] = useState(false);
  const [videoStreamFailed, setVideoStreamFailed] = useState(false);
  const [isCheckingOut, setIsCheckingOut]       = useState(false);
  const {
    videoPlaying,
    videoEnded,
    videoReady,
    engaged: ctaEngaged,
    setVideoReady,
    onVideoPlay: onEngagementPlay,
    onVideoEnded: onEngagementEnded,
    onPlaybackTelemetry,
  } = useMembershipCtaEngagement();
  // Ref to read video state inside stable callbacks (avoids adding videoPlaying/
  // videoEnded to useCallback deps and recreating on every video frame transition).
  const videoStateRef = useRef({ playing: false, ended: false });
  useEffect(() => {
    videoStateRef.current = { playing: videoPlaying, ended: videoEnded };
  }, [videoPlaying, videoEnded]);
  // ctaMounted: arrancamos en `true` para que el panel de CTAs (VIP + Free)
  // sea visible desde el PRIMER paint. Antes arrancaba en `false` y se
  // activaba a true tras 60ms (efecto fade-in slide-up). Si el setTimeout no
  // disparaba a tiempo o el componente unmounteaba antes, los botones
  // quedaban con `opacity-0` invisibles. Para garantizar que los 2 botones
  // SIEMPRE se vean en mobile/PWA, eliminamos el delay — el visitor los ve
  // sin animation cost adicional (negligible en TikTok-shop layout donde el
  // video llena la pantalla).
  const [ctaMounted, setCtaMounted]             = useState(true);
  const [embeddedSocialBrowser, setEmbeddedSocialBrowser] = useState(false);
  const didApplyIgCssRef = useRef(false);

  // ── Worker-driven labels + visibility (no hardcode pathname → surface) ────
  // El worker decide la surface (intl|serMiembro) desde el pathname del request
  // y pre-mergea las settings de /admin/dual-cta-visibility per-surface +
  // labels admin DB (stripe_checkout_config) lang-correct antes de inyectarlas
  // (templateConfig + snapshot). Si KV vacío → liveCTA.* es null y el front
  // cae a los hooks fallback (useDualCtaVisibility / useDualCtaLabels Railway).
  const visibilityFallback = useDualCtaVisibility();
  // Admin labels DB cascade: worker live KV → hook Railway fallback → i18n default.
  // `fallbackAdminLabels` (useDualCtaLabels) está declarado arriba, junto a la
  // cascada de playbackId que también lo consume.
  const workerAdminLabels = liveCTA.dualCtaLabels;
  const adminLabels = {
    paid: workerAdminLabels?.paid ?? fallbackAdminLabels.paid,
    free: workerAdminLabels?.free ?? fallbackAdminLabels.free,
  };
  const workerSurfaceVisibility = liveCTA.dualCtaVisibility;
  const surfaceFromWorker = liveCTA.dualSurface;
  // Fallback usado SOLO cuando el worker no resolvió la visibility:
  //   - surface = la que el worker dijo, si la dijo
  //   - sino: sin path en el snapshot el worker assume `intl`, así que la
  //     fallback respeta esa convención (intl). Único caso donde el front
  //     "elige" surface es cuando el worker está caído O en preview SSR-less,
  //     y elegimos el caso mayoritario (intl `/`) para no romper.
  const surfaceFallbackKey = (surfaceFromWorker === "serMiembro" ? "serMiembro" : "intl") as
    | "intl"
    | "serMiembro";
  const fallbackSurface = visibilityFallback[surfaceFallbackKey];
  const overrideVipLabelLang = workerSurfaceVisibility
    ? workerSurfaceVisibility.vipLabel
    : lang === "en"
      ? fallbackSurface.vipLabelEn
      : fallbackSurface.vipLabelEs;
  const overrideFreeLabelLang = workerSurfaceVisibility
    ? workerSurfaceVisibility.freeLabel
    : lang === "en"
      ? fallbackSurface.freeLabelEn
      : fallbackSurface.freeLabelEs;
  const effectiveVipLabel   = overrideVipLabelLang || variant?.vipLabel    || apiVipLabel   || adminLabels.paid || localT("vipLabel",  lang) || (lang === "es" ? intlFallback.vipLabelEs : intlFallback.vipLabelEn);
  const effectiveFreeLabel  = overrideFreeLabelLang || variant?.freeLabel   || apiFreeTelegramLabel || adminLabels.free || localT("freeLabel", lang);
  // Subtítulo VIP: variante (worker/SPA) > fallback REST geo-cascade > i18n
  // default. Sin `dual_cta_visibility` override (esa tabla no tiene columna
  // de subtítulo, solo label). Antes este campo se ignoraba por completo acá
  // — un admin que cargaba subtítulo (incl. para una ciudad custom) en
  // /admin/dual-cta-variants nunca lo veía reflejado en este panel.
  const effectiveVipSubtitle =
    variant?.vipSubtitle || fallbackAdminLabels.paidSubtitle || localT("vipSubtitle", lang);
  const brandDisplayName    = BRAND_NAME || BRAND_PERSON_NAME || "";

  // ── Hero conversion gate (IP/geo restrictions, AR-focused) ──────────────────
  // Tiered:
  //   bot/datacenter  → ocultar paid + free
  //   hideCta (VPN)   → ocultar paid + free
  //   anonymous       → ocultar solo paid (free Telegram queda)
  // AR detail: iplocate Capa 2 emite isHosting/isAnonymous para visitors AR;
  // non-AR depende del Worker emitiendo hideCta (VPN provider + timezone).
  // (liveCTA ya está obtenido arriba para la cascada de labels — reusamos.)
  const baseHeroGate = useMemo(
    () => heroConversionGate(getServerTemplateConfig(), liveCTA),
    [liveCTA],
  );

  // ── Override gate para /ser-miembro: solo bot bloquea ──────────────────────
  // DualCTAVideoLanding ES el CTA — el visitor llegó acá porque tipeó la URL,
  // clickeó el heroCta de la home AR, o vio un anuncio. Suprimir los 2 botones
  // por `hideCta` (VPN provider + timezone mismatch) o `anonymous` (Tor/iCloud
  // Relay) convierte el destino en dead-end: el visitor está en /ser-miembro
  // mirando un video con 0 acciones disponibles.
  //
  // Solo bloqueamos cuando `reason === "bot"` (datacenter/scraper sin valor)
  // — para humanos privacy-conscious (VPN, Tor, iCloud Relay) los botones
  // SIEMPRE están disponibles. El checkout interno valida el pago igual.
  //
  // En contraste, las HOME pages (AR home + intl home con VideoTemplate) sí
  // respetan el gate completo porque el visitor todavía está navegando, no
  // tomó la decisión de convertir.
  const heroGate = useMemo(
    () =>
      baseHeroGate.reason === "bot"
        ? baseHeroGate
        : { hidePaid: false, hideFree: false, reason: "ok" as const },
    [baseHeroGate],
  );

  // ── Admin preview / geo-sim Clean: forzar CTAs visibles ─────────────────
  // ?_force_variant / ?_admin_preview=1 → preview admin embebido.
  // ?geo_sim=1&simulate_vpn=clean → toolbar "Mode Clean" debe verse como
  // visitor limpio del país (VIP membership CTA siempre), aunque simulate_ip
  // sea un Tor exit usado solo para metadata de QA.
  const simSearchParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : null;
  const simulateVpnParam = (
    simSearchParams?.get("simulate_vpn") ||
    simSearchParams?.get("vpn_sim") ||
    ""
  )
    .toLowerCase()
    .trim();
  const isGeoSimClean =
    simSearchParams?.get("geo_sim") === "1" &&
    (simulateVpnParam === "clean" || simulateVpnParam === "");
  const isAdminPreview =
    typeof window !== "undefined" &&
    (!!simSearchParams?.get("_force_variant") ||
      simSearchParams?.get("_admin_preview") === "1");
  const forceShowConversionCtas =
    isAdminPreview || isGeoSimClean || isSuperAdmin;

  // Visibility flags pre-mergeados por el worker (KV-driven). Si KV vacío
  // (`workerSurfaceVisibility === null`), caemos al fallback Railway para la
  // surface que el worker señaló (o intl si no señaló — caso preview).
  // Estado "gris" (admin disabled): botón visible pero no clickeable.
  // Distinto de !enabled que oculta totalmente.
  const vipDisabledByAdmin = workerSurfaceVisibility
    ? workerSurfaceVisibility.vipDisabled
    : fallbackSurface.vipDisabled;
  const freeDisabledByAdmin = workerSurfaceVisibility
    ? workerSurfaceVisibility.freeDisabled
    : fallbackSurface.freeDisabled;
  const surfaceVipEnabled = workerSurfaceVisibility
    ? workerSurfaceVisibility.vipEnabled
    : fallbackSurface.vipEnabled;
  // USExclusiveLanding = página de conversión (mobile + desktop 9:16).
  // El VIP membership CTA debe verse SIEMPRE salvo early-return VpnBlockedScreen.
  // No depende de heroGate bot/hosting ni de surface toggle: sin VIP la landing
  // es un dead-end (video sin compra) en mobile y desktop, real y geo-sim.
  // DualCTA /ser-miembro sigue el gate normal + forceShow en preview/clean.
  const showVip = isUsExclusiveHost
    ? true
    : forceShowConversionCtas
      ? true
      : !heroGate.hidePaid && surfaceVipEnabled;
  // Free solo si la variante lo habilita. En exclusive suele ser false → no
  // pintar free gris (antes forceShowConversionCtas + onFreeClick siempre
  // metía un botón free vacío en mobile y tapaba el foco del VIP).
  const showFreeButton = isUsExclusiveHost
    ? freeEnabledByVariant && !freeDisabledByAdmin
    : forceShowConversionCtas
      ? true
      : !heroGate.hideFree;
  const freeIsGrey = !freeEnabledByVariant || freeDisabledByAdmin;
  // Exclusive: nunca deshabilitar VIP por surface admin (la landing ES el checkout).
  const vipButtonDisabled = isUsExclusiveHost ? false : vipDisabledByAdmin;

  // ── Funnel tracking ref ───────────────────────────────────────────────────────
  const funnelRef = useRef({
    enteredAt:          Date.now(),
    exitFired:          false,
    videoStarted:       false,
    videoCompleted:     false,
    ctaVip:             false,
    ctaFree:            false,
    /** true si onIntent stripe disparó checkout_opened antes del redirect */
    checkoutOpened:     false,
    /** true si API falló / fan_fallback / exception */
    checkoutFailed:     false,
    checkoutError:      "" as string,
    firstVipClickMs:    0,   // ms from page enter → first VIP click
    firstFreeClickMs:   0,   // ms from page enter → first Free click
    firstPlayMs:        0,   // ms from page enter → first video play
  });

  const fireAlert = useCallback(
    (kind: string, extra: Record<string, string | number | boolean> = {}) => {
      try {
        const cfg    = getServerTemplateConfig();
        const countryCode = (cfg?.country ?? getVisitorCountry() ?? "UNKNOWN").toUpperCase();
        const expectedGateway = "stripe";
        const expectedCurrency = "USD";
        const workerLanguage = cfg?.language ?? "unknown";
        const countryTemplateRegion = resolveCountryTemplateRegion(countryCode);
        const ts     = detectTrafficSource();
        const phDiag = getSlackFunnelDiagnosticsExtraPostHog();
        const f      = funnelRef.current;

        // ── Always-present session context (device, attribution, confidence) ─────
        const referrerDomain = (() => {
          try {
            const ref = document.referrer;
            if (!ref) return "";
            return new URL(ref).hostname.replace(/^www\./, "");
          } catch { return ""; }
        })();
        const connectionType = (() => {
          try {
            return (navigator as { connection?: { effectiveType?: string } })
              .connection?.effectiveType ?? "unknown";
          } catch { return "unknown"; }
        })();
        const isStandalonePWA = (() => {
          try {
            return window.matchMedia("(display-mode: standalone)").matches ||
              (navigator as { standalone?: boolean }).standalone === true;
          } catch { return false; }
        })();
        const workerUsState = (cfg?.usState ?? "").trim().toUpperCase() || "";
        const sessionCtx: Record<string, string | number | boolean> = {
          device_type:        deviceClass,
          device_os:          deviceOS,
          screen_width:       typeof window !== "undefined" ? window.screen.width : 0,
          is_return_visitor:  isReturnVisitor,
          country_confidence: getCountryConfidenceSource(),
          referrer_domain:    referrerDomain,
          connection_type:    connectionType,
          is_standalone_pwa:  isStandalonePWA,
          // US state (country=US only) — drives state_us cascade en variant resolution.
          worker_us_state:    workerUsState,
          // Variant pricing seen by this visitor (always log even if 0)
          variant_price_usd:  variant?.priceUsd ?? intlFallback.priceUsd,
          variant_has_video:  !!(variant?.muxPlaybackId),
          variant_display_name: variant?.displayName ?? "",
          ...utmParams,
        };

        trackDualCtaVideoLandingPostHog(kind as DualCtaVideoLandingAction, {
          template:             hostTemplate,
          template_type:        hostTemplate,
          country:              countryCode,
          worker_language:      workerLanguage,
          expected_gateway:     expectedGateway,
          expected_currency:    expectedCurrency,
          country_template_region: countryTemplateRegion,
          priority_stripe_dual_market: PRIORITY_STRIPE_DUAL_COUNTRIES.has(countryCode),
          time_on_page_seconds: Math.round((Date.now() - f.enteredAt) / 1000),
          video_started:        f.videoStarted,
          video_completed:      f.videoCompleted,
          cta_vip_clicked:      f.ctaVip,
          cta_free_clicked:     f.ctaFree,
          language:             lang,
          source:               ts.source,
          medium:               ts.medium,
          channel:              ts.channel,
          // Dual A/B attribution per event (super-props ya están registradas
          // pero las repetimos en extra para que aparezcan en eventos legados
          // que filtran por props específicas y no leen super-props).
          ...(variant?.id ? {
            dual_variant_id: variant.id,
            dual_variant_geo_scope: variant.assignmentScope,
            dual_variant_geo_code: variant.assignmentGeo,
          } : {}),
          ...(abBucket ? { dual_ab_bucket: abBucket } : {}),
          ...sessionCtx,
          ...extra,
        });
        void fetch("/api/track-funnel-failure", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          keepalive: true,
          body: JSON.stringify({
            kind,
            template: hostTemplate,
            country: countryCode,
            time_on_page_seconds: Math.round((Date.now() - f.enteredAt) / 1000),
            page_url: typeof window !== "undefined" ? window.location.href : undefined,
            extra: {
              source:          ts.source,
              medium:          ts.medium,
              channel:         ts.channel,
              expected_gateway: expectedGateway,
              expected_currency: expectedCurrency,
              country_template_region: countryTemplateRegion,
              priority_stripe_dual_market: PRIORITY_STRIPE_DUAL_COUNTRIES.has(countryCode),
              referrer:        (document.referrer || "").slice(0, 300),
              worker_city:     cfg?.city ?? "",
              worker_carrier:  cfg?.asOrganization ?? "",
              worker_language: workerLanguage,
              video_started:   f.videoStarted,
              video_completed: f.videoCompleted,
              language:        lang,
              // Dual A/B → Slack footer + DB persistence.
              dual_variant_id: variant?.id,
              dual_variant_geo_scope: variant?.assignmentScope,
              dual_variant_geo_code: variant?.assignmentGeo,
              dual_ab_bucket: abBucket,
              ...sessionCtx,
              ...phDiag,
              ...extra,
            },
          }),
        }).catch(() => {});
      } catch {
        // best-effort
      }
    },
    [lang, variant, abBucket, deviceClass, deviceOS, isReturnVisitor, utmParams, hostTemplate],
  );

  // ── Estado de disponibilidad de checkout ────────────────────────────────────
  const [checkoutAvailable, setCheckoutAvailable] = useState<boolean | null>(null);
  const [paymentGateway, setPaymentGateway] = useState<"stripe" | null>(null);

  // Detectar país y disponibilidad de gateway al montar.
  //
  // Source of truth: window.__TEMPLATE_CONFIG__.country (Cloudflare Worker
  // → cf.country → KV) so the admin can flip behavior in real time from the
  // dashboard. We do NOT call /api/detect-country here — reading
  // x-vercel-ip-country directly would bypass the admin's KV-driven control
  // and lock the routing to whatever Railway sees per request.
  //
  // When the worker isn't on the path the sync chain falls through to
  // detectCountryCode() (timezone / browser lang). That's intentionally
  // imperfect — the long-term fix is activating the worker on the apex
  // route, not adding more client-side IP detection.
  useEffect(() => {
    const country = getVisitorCountry();

    setPaymentGateway("stripe");

    const hasApiEndpoint = true;
    setCheckoutAvailable(hasApiEndpoint);

    ["https://checkout.stripe.com", "https://js.stripe.com"].forEach((href) => {
      const link = document.createElement("link");
      link.rel = "preconnect";
      link.href = href;
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);
    });

    console.log("[DualCTAVideoLanding] Payment detection:", {
      country,
      gateway: "stripe",
      source: country === "UNKNOWN" ? "fallback" : "worker_or_timezone",
      checkoutAvailable: hasApiEndpoint,
    });
  }, []);

  // Pre-checkout email modal → real email on intent + capture-checkout-email.
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  // ── VIP checkout: create Stripe session (email optional but preferred) ─────
  const runVipCheckout = useCallback(
    async (buyerEmail?: string | null) => {
      if (isCheckingOut || !checkoutAvailable) return;
      funnelRef.current.ctaVip = true;

      const country = getVisitorCountry();

      if (!funnelRef.current.firstVipClickMs) {
        funnelRef.current.firstVipClickMs =
          Date.now() - funnelRef.current.enteredAt;
      }
      fireAlert("cta_click_vip", {
        gateway: "stripe_api",
        cta_source: "checkout_api",
        country,
        time_to_click_seconds: Math.round(
          funnelRef.current.firstVipClickMs / 1000,
        ),
        video_was_playing: videoStateRef.current.playing,
        video_was_ended: videoStateRef.current.ended,
        has_pre_checkout_email: Boolean(buyerEmail),
      });
      trackAddToCartDual({
        value: variant?.priceUsd ?? intlFallback.priceUsd,
        currency: "USD",
        variantId: variant?.id,
        contentName: "VIP video membership",
      });
      trackInitiateCheckoutDual({
        value: variant?.priceUsd ?? intlFallback.priceUsd,
        currency: "USD",
        contentName: "VIP video membership",
      });

      setIsCheckingOut(true);
      setEmailModalOpen(false);
      try {
        const languageForPlans = isUsExclusiveHost
          ? "en-US"
          : lang === "es"
            ? "es-AR"
            : "en-US";

        const variantFromUrl =
          new URLSearchParams(window.location.search).get("v")?.trim() ||
          undefined;

        const stripeCheckout = resolveVipStripeCheckoutFromFallback(
          variant,
          intlFallback,
        );

        const onCheckoutIntent = (detail: {
          gateway: string;
          error?: string;
          code?: string;
        }) => {
          if (detail.gateway === "stripe" || detail.gateway === "mercadopago") {
            funnelRef.current.checkoutOpened = true;
            fireAlert("checkout_opened", {
              gateway: detail.gateway,
              dual_variant_id: stripeCheckout.dualVariantId,
              price_id: stripeCheckout.stripePriceIdOverride ?? "",
            });
          } else {
            const errDetail =
              detail.code || detail.error || detail.gateway || "fan_fallback";
            funnelRef.current.checkoutFailed = true;
            funnelRef.current.checkoutError = errDetail;
            fireAlert("cta_checkout_error", {
              gateway: detail.gateway,
              error:
                detail.error || detail.code || "fan_fallback_or_blocked",
              error_code: detail.code || "",
              dual_variant_id: stripeCheckout.dualVariantId,
              price_id: stripeCheckout.stripePriceIdOverride ?? "",
              promotion_code: stripeCheckout.stripePromotionCodeId || "",
            });
          }
        };

        const email = buyerEmail?.trim() || undefined;

        await (isArVisitor
          ? startVipMembershipVideoCheckout({
              countryCode: country,
              pathname: window.location.pathname,
              languageForPlans,
              workerTemplate: hostTemplate,
              variant: variant?.mpVariantLabel || variantFromUrl,
              variantPriceUsd: variant?.priceUsd ?? intlFallback.priceUsd,
              stripePriceIdOverride: stripeCheckout.stripePriceIdOverride,
              stripePromotionCodeId: stripeCheckout.stripePromotionCodeId,
              dualVariantId: stripeCheckout.dualVariantId,
              dualVariantGeoScope: variant?.assignmentScope,
              dualVariantGeoCode: variant?.assignmentGeo,
              dualAbBucket: abBucket,
              email,
              productNameOverride: intlFallback.stripeProductName,
              discountRate:
                stripeCheckout.stripePromotionCodeId || !specialOfferActive
                  ? 0
                  : VIP_DISCOUNT_RATE,
              onIntent: onCheckoutIntent,
            })
          : launchIntlExclusiveMembershipCheckout({
              countryCode: country,
              pathname: window.location.pathname,
              languageForPlans,
              workerTemplate: hostTemplate,
              email,
              dualAbBucket: abBucket,
              onIntent: onCheckoutIntent,
              variant: variant
                ? {
                    id: variant.id,
                    stripePriceId: variant.stripePriceId,
                    stripePromotionCodeId: variant.stripePromotionCodeId,
                    priceUsd: variant.priceUsd,
                    assignmentScope: variant.assignmentScope,
                    assignmentGeo: variant.assignmentGeo,
                    mpVariantLabel: variant.mpVariantLabel || variantFromUrl,
                  }
                : variantFromUrl
                  ? { mpVariantLabel: variantFromUrl }
                  : null,
            }));
      } catch (error) {
        console.error("[DualCTAVideoLanding] Checkout error:", error);
        funnelRef.current.checkoutFailed = true;
        const errMsg = error instanceof Error ? error.message : "unknown";
        funnelRef.current.checkoutError = errMsg;
        fireAlert("cta_checkout_error", {
          gateway: "stripe",
          error: errMsg,
        });
      } finally {
        setTimeout(() => setIsCheckingOut(false), 500);
      }
    },
    [
      isCheckingOut,
      checkoutAvailable,
      fireAlert,
      variant,
      abBucket,
      lang,
      specialOfferActive,
      hostTemplate,
      isUsExclusiveHost,
      intlFallback,
      isArVisitor,
    ],
  );

  // Gate: sin email conocido → modal pre-checkout (API + intent + PostHog).
  const handleVipClick = useCallback(() => {
    if (isCheckingOut || !checkoutAvailable) return;
    const known = resolvePreCheckoutEmail(
      isSignedIn ? user?.primaryEmailAddress?.emailAddress : null,
    );
    if (known) {
      void runVipCheckout(known);
      return;
    }
    setEmailModalOpen(true);
  }, [isCheckingOut, checkoutAvailable, isSignedIn, user, runVipCheckout]);

  // Landings SEO y CTAs externos pueden traer ?checkout=vip → abrir Stripe al montar.
  const autoCheckoutFiredRef = useRef(false);
  useEffect(() => {
    if (embeddedPreview || shouldForceBaseTemplate) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") !== "vip") return;
    if (autoCheckoutFiredRef.current || !checkoutAvailable) return;
    autoCheckoutFiredRef.current = true;
    void handleVipClick();
  }, [checkoutAvailable, handleVipClick, embeddedPreview, shouldForceBaseTemplate]);

  // ── Preconnect to Mux ────────────────────────────────────────────────────────
  useEffect(() => {
    const hosts = [
      "https://stream.mux.com",
      "https://image.mux.com",
      "https://inferred.litix.io",
    ];
    const links: HTMLLinkElement[] = [];
    for (const href of hosts) {
      for (const rel of ["preconnect", "dns-prefetch"]) {
        const link = document.createElement("link");
        link.rel = rel;
        link.href = href;
        if (rel === "preconnect") link.crossOrigin = "anonymous";
        document.head.appendChild(link);
        links.push(link);
      }
    }
    return () => {
      for (const l of links) {
        if (document.head.contains(l)) document.head.removeChild(l);
      }
    };
  }, []);

  // ── Instagram browser ───────────────────────────────────────────────────────
  useEffect(() => {
    if (isInstagramBrowser()) {
      setEmbeddedSocialBrowser(true);
      if (!didApplyIgCssRef.current) {
        didApplyIgCssRef.current = true;
        applyInstagramBrowserCSS();
      }
    }
  }, []);

  // ── Template context ────────────────────────────────────────────────────────
  useEffect(() => {
    setTemplateName(hostTemplate);
    return () => setTemplateName(null);
  }, [setTemplateName, hostTemplate]);

  // Admin + producción: liveCTA.dualVariant (via hook) reemplaza mux/labels/precio in-place.
  // El hook ya ejecuta el fallback SPA cuando corresponde.

  // ── PostHog: experiencia completa (template + dual + cascade + surface) ───
  // Re-registra cuando la variante live del poll cambia (misma pestaña).
  // dualSurface del worker (intl|serMiembro) va en super-props para filtrar.
  // Promote visitor_experience_resolved when membership deep-link settles
  // (SessionIngress only had worker seed without dual).
  useEffect(() => {
    const dualVariantPayload = variant
      ? {
          id: variant.id,
          displayName: variant.displayName,
          muxPlaybackId: variant.muxPlaybackId,
          priceUsd: variant.priceUsd,
          stripePriceId: variant.stripePriceId,
          mpVariantLabel: variant.mpVariantLabel,
          assignmentScope: variant.assignmentScope,
          assignmentGeo: variant.assignmentGeo,
          freeEnabled: variant.freeEnabled,
          freeTelegramUrl: variant.freeTelegramUrl,
        }
      : null;
    void import("@/lib/visitorExperiencePostHog").then(
      ({
        registerVisitorExperienceContextPostHog,
        trackVisitorExperienceResolvedPostHog,
      }) => {
        registerVisitorExperienceContextPostHog({
          effectiveTemplate: hostTemplate,
          dualSurface: surfaceFromWorker ?? null,
          dualVariant: dualVariantPayload,
          abBucket,
        });
        trackVisitorExperienceResolvedPostHog({
          effectiveTemplate: hostTemplate,
          dualSurface: surfaceFromWorker ?? null,
          dualVariant: dualVariantPayload,
          abBucket,
          force: true,
        });
      },
    );
    if (!variant?.id) return;
    registerDualCtaVariantContextPostHog({
      dual_variant_id: variant.id,
      dual_variant_geo_scope: variant.assignmentScope,
      dual_variant_geo_code: variant.assignmentGeo,
      dual_ab_bucket: abBucket,
      dual_variant_stripe_price_id: variant.stripePriceId,
      dual_variant_mp_label: variant.mpVariantLabel,
      dual_variant_price_usd: variant.priceUsd,
    });
  }, [variant, abBucket, hostTemplate, surfaceFromWorker]);

  // ── CTA entrance animation ──────────────────────────────────────────────────
  // Eliminado: el setTimeout(60ms) que activaba el fade-in del CTA panel ya
  // no hace falta porque `ctaMounted` arranca en `true` (ver línea ~290).
  // Garantizar que los 2 botones se vean SIEMPRE al primer paint sin
  // depender de timers que pueden no dispararse en algunos devices/PWA.

  // ── Body lock (iOS scroll bleed-through) ────────────────────────────────────
  // Lockea body para que iOS Safari + Instagram WebView no permitan scroll
  // bleed-through detrás del fullscreen modal. Las 3 height candidates
  // (dvh > vh > -webkit-fill-available) cubren todo: iOS 15+ (dvh OK),
  // iOS 13-14 (sin dvh → vh OK), iOS Safari viejos (sin dvh ni vh estable
  // por URL bar → fill-available). El browser se queda con la primera que
  // entiende. `top: 0; left: 0` evitan que un parent con scroll-position
  // existente desplace el body al hacer position: fixed.
  useEffect(() => {
    if (embeddedPreview) return;
    const saved = {
      overflow: document.body.style.overflow,
      height:   document.body.style.height,
      position: document.body.style.position,
      width:    document.body.style.width,
      top:      document.body.style.top,
      left:     document.body.style.left,
    };
    document.body.style.overflow = "hidden";
    document.body.style.height   = "100dvh";
    document.body.style.position = "fixed";
    document.body.style.width    = "100%";
    document.body.style.top      = "0";
    document.body.style.left     = "0";
    return () => {
      document.body.style.overflow = saved.overflow;
      document.body.style.height   = saved.height;
      document.body.style.position = saved.position;
      document.body.style.width    = saved.width;
      document.body.style.top      = saved.top;
      document.body.style.left     = saved.left;
    };
  }, [embeddedPreview]);

  // Título distintivo en la tab/history → trackeable y pedible a usuarios
  // (default index.html title se restaura automáticamente al desmontar).
  useDualVariantTitle(isUsExclusiveHost ? "us" : "global");

  // ── Page enter alert ────────────────────────────────────────────────────────
  useEffect(() => {
    if (isUsExclusiveHost) {
      trackPageViewPostHog("us_exclusive_landing", document.title, hostTemplate);
    } else {
      // pathname dinámico — /ser-miembro (AR) o /membership (intl).
      trackPageViewPostHog(
        typeof window !== "undefined"
          ? window.location.pathname
          : getMembershipLandingPath(),
        document.title,
        hostTemplate,
      );
    }
    fireAlert("page_enter_dual_cta_video", {
      // Snapshot of what the visitor sees at entry (variant may change via SPA)
      surface:                   surfaceFromWorker ?? "unknown",
      playback_id_set:           !!playbackId,
      free_enabled:              freeEnabledByVariant,
      variant_id_at_enter:       variant?.id ?? "",
      variant_price_usd_at_enter: variant?.priceUsd ?? 0,
      variant_has_video_at_enter: !!(variant?.muxPlaybackId),
      ab_bucket_at_enter:        abBucket ?? "",
      show_vip_at_enter:         showVip,
      show_free_at_enter:        showFreeButton && freeEnabledByVariant,
      // US state presente en entrada — crítico para analizar conversion por estado.
      us_state_at_enter: (getServerTemplateConfig()?.usState ?? "").trim().toUpperCase() || "",
      ...(isUsExclusiveHost
        ? { is_rest_of_world_us_exclusive: visitorCountry !== "US" }
        : {}),
    });
    // Visibility issue: visitor entró pero playbackId quedó null (variant
    // sin mux + sin ENV_PLAYBACK_ID). Sin esto admin no se entera de
    // variantes incompletas — Slack #juaperrone + PostHog se enteran al
    // primer visitor afectado para que arregle rápido.
    if (!playbackId) {
      fireAlert("mux_playback_id_missing", {
        variant_id: variant?.id ?? "",
        variant_geo_scope: variant?.assignmentScope ?? "",
        variant_geo_code: variant?.assignmentGeo ?? "",
        env_playback_id_set: !!ENV_PLAYBACK_ID,
      });
    }
    // Meta ViewContent — dispara browser Pixel + server CAPI deduplicado.
    trackDualEvent("ViewContent", {
      content_name: "DualCTA · VIP video landing",
      content_category: "membership_dual",
      content_type: "product_group",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Session exit alert ──────────────────────────────────────────────────────
  useEffect(() => {
    const fireExit = (reason: string) => {
      const f = funnelRef.current;
      if (f.exitFired) return;
      f.exitFired = true;
      // drop_reason = por qué no convirtió (fuente de verdad para auditorías).
      // Orden: checkout outcome → VIP intent → free → video → bounce.
      let dropReason: string;
      let segment: string;
      if (f.checkoutOpened) {
        dropReason = "left_at_stripe_checkout";
        segment = "converted_to_gateway";
      } else if (f.checkoutFailed) {
        dropReason = f.checkoutError
          ? `checkout_failed:${f.checkoutError.slice(0, 80)}`
          : "checkout_failed";
        segment = "checkout_error";
      } else if (f.ctaVip) {
        // Tras fix onIntent debería ser raro; si aparece = tracking race o API colgó.
        dropReason = "vip_click_no_checkout_opened";
        segment = "vip_intent_no_gateway";
      } else if (f.ctaFree) {
        dropReason = "free_only_no_vip";
        segment = "free_channel_only";
      } else if (f.videoCompleted) {
        dropReason = "video_complete_no_vip";
        segment = "saw_video_no_cta";
      } else if (f.videoStarted) {
        dropReason = "video_play_no_vip";
        segment = "other_engagement";
      } else {
        dropReason = "bounce_no_video";
        segment = "bounce";
      }
      fireAlert("session_exit", {
        abandon_segment: segment,
        drop_reason:     dropReason,
        exit_reason:     reason,
        cta_vip_clicked:  f.ctaVip,
        cta_free_clicked: f.ctaFree,
        checkout_opened:  f.checkoutOpened,
        checkout_failed:  f.checkoutFailed,
        time_to_vip_click_seconds:   f.firstVipClickMs  ? Math.round(f.firstVipClickMs  / 1000) : -1,
        time_to_free_click_seconds:  f.firstFreeClickMs ? Math.round(f.firstFreeClickMs / 1000) : -1,
        time_to_video_start_seconds: f.firstPlayMs      ? Math.round(f.firstPlayMs      / 1000) : -1,
      });
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") fireExit("visibility_hidden");
    };
    const onPagehide = () => fireExit("pagehide");
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPagehide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPagehide);
    };
    // fireAlert in deps → cuando variant cambia (SPA fallback), reregistramos
    // listener para que session_exit reporte la variant correcta. f.exitFired
    // guard previene doble disparo si el effect re-corre antes del exit real.
  }, [fireAlert]);

  // ── API config ──────────────────────────────────────────────────────────────
  // Si la variante A/B ya define muxPlaybackId / labels, NO los pisamos con la
  // config global — la variante gana. Solo usamos la API global para los
  // campos que la variante no setea (free_telegram_url, dual_cta_free_enabled).
  useEffect(() => {
    fetchPremiumInvitePublicConfig()
      .then((cfg) => {
        if (!cfg) return;
        // Race-safe: leemos variantRef.current en vez del `variant` capturado
        // en mount. Si SPA fallback resolvió variant mientras este fetch corría,
        // vemos el valor nuevo y NO pisamos los fields que la variante define.
        const v = variantRef.current;
        const pid = cfg.simple_mux_overlay_playback_id?.trim() || null;
        const ftUrl = cfg.free_telegram_url?.trim() || null;
        const vipLbl = cfg.end_screen_vip_cta_label?.trim() || null;
        if (vipLbl && !v?.vipLabel) setApiVipLabel(vipLbl);
        const freeLbl = cfg.end_screen_free_cta_label?.trim() || null;
        if (freeLbl && !v?.freeLabel) setApiFreeTelegramLabel(freeLbl);
        // free_telegram_url global solo como fallback de URL; el enable es por variante.
        setGlobalInviteFallback({
          playbackId: pid,
          freeTelegramUrl: ftUrl,
          freeEnabled: null,
        });
        registerDualCtaVideoLandingContextPostHog({
          dual_cta_free_enabled: v?.freeEnabled === true,
          dual_cta_has_playback_id: Boolean(v?.muxPlaybackId || pid || ENV_PLAYBACK_ID),
          dual_cta_has_free_url: Boolean(ftUrl || ENV_FREE_TELEGRAM_URL),
        });
      })
      .catch(() => {});
  }, []);

  // El video es complementario: si falta el playbackId (env var sin setear o
  // /api/premium-invite-config sin respuesta) NO bloqueamos la página — los dos
  // CTAs (VIP + gratis) deben mostrarse igual. La columna de video cae a un fondo
  // de marca cuando no hay video.
  const hasVideo = Boolean(playbackId);
  const showVideoLoadingOverlay =
    hasVideo && !videoPlaying && !videoEnded && !videoNeedsGesture && !videoStreamFailed;

  const onVideoPlayerNeedsGesture = useCallback(() => {
    setVideoNeedsGesture(true);
    setVideoReady(true);
  }, [setVideoReady]);

  const onVideoPlayerError = useCallback(
    (message: string) => {
      setVideoStreamFailed(true);
      setVideoNeedsGesture(true);
      setVideoReady(true);
      // Slack + PostHog: MuxVideoPlayerWithCTA → trackMuxVideoFailureSlack (mux_playback_error)
    },
    [setVideoReady],
  );

  // ── Shared video event handlers ──────────────────────────────────────────────
  // Tracking en 2 surfaces:
  //   1. PostHog (trackVideoPlay/CompletePostHog) — usado por front-metrics /
  //      report-recent dashboards (engagement metrics, conversion ratios).
  //   2. Slack #juaperrone (fireAlert) — alerta tiempo real para que el equipo
  //      vea engagement durante el funnel. Cierra el loop: page_enter →
  //      video_play → cta_click → checkout_opened → payment_completed.
  const onVideoPlay = () => {
    onEngagementPlay();
    const firstPlay = !funnelRef.current.videoStarted;
    funnelRef.current.videoStarted = true;
    if (firstPlay) {
      if (!funnelRef.current.firstPlayMs) {
        funnelRef.current.firstPlayMs = Date.now() - funnelRef.current.enteredAt;
      }
      trackVideoPlayPostHog(
        playbackId ?? "dual_cta",
        "DualCTA VIP video",
        playbackId ?? undefined,
        hostTemplate,
        getVisitorCountry(),
      );
      fireAlert("video_play", {
        playback_id: playbackId ?? "",
        variant_id: variant?.id ?? "",
        time_to_play_seconds: Math.round(funnelRef.current.firstPlayMs / 1000),
      });
      // Meta Pixel + CAPI custom event — feed para Audiences "vio video".
      // content_ids=[variant_id] permite a Meta Audiences segmentar A/B/C
      // automático y retargetear con campañas específicas per variante.
      trackVideoViewStartedDual({
        variantId: variant?.id,
        playbackId: playbackId ?? undefined,
        country: getVisitorCountry(),
      });
    }
  };

  const onVideoEnded = () => {
    onEngagementEnded();
    const firstComplete = !funnelRef.current.videoCompleted;
    funnelRef.current.videoCompleted = true;
    if (firstComplete) {
      const watchedSeconds = Math.round(
        (Date.now() - funnelRef.current.enteredAt) / 1000,
      );
      trackVideoCompletePostHog(
        playbackId ?? "dual_cta",
        watchedSeconds,
        hostTemplate,
        getVisitorCountry(),
      );
      fireAlert("video_complete", {
        playback_id: playbackId ?? "",
        variant_id: variant?.id ?? "",
        watched_seconds: watchedSeconds,
      });
      // Meta Pixel + CAPI custom event — "vieron video completo" es la señal
      // de intent más alta pre-checkout (~3-5× conversion vs solo VideoStarted).
      // Esta audience es la candidata #1 para campañas de retargeting Meta.
      trackVideoViewCompletedDual({
        variantId: variant?.id,
        playbackId: playbackId ?? undefined,
        watchedSeconds,
        country: getVisitorCountry(),
      });
    }
  };

  const handleFreeChannelClick = useCallback(() => {
    // Free off por defecto: el botón puede verse en gris sin navegar.
    if (!freeEnabledByVariant || freeDisabledByAdmin) return;

    funnelRef.current.ctaFree = true;
    if (!funnelRef.current.firstFreeClickMs) {
      funnelRef.current.firstFreeClickMs = Date.now() - funnelRef.current.enteredAt;
    }
    fireAlert("cta_click_free_channel", {
      cta_url: freeTelegramUrl ?? "",
      time_to_click_seconds: Math.round(funnelRef.current.firstFreeClickMs / 1000),
      video_was_playing: videoStateRef.current.playing,
      video_was_ended: videoStateRef.current.ended,
    });
    trackLeadDual({ contentName: "telegram_dual_free" });

    const vid = variant?.id ?? "";
    if (!isSignedIn) {
      navigate(
        `/registro${buildMembershipFunnelQuery({ plan_tier: "free", variant_id: vid })}`,
      );
      return;
    }
    if (!hasMembershipTelegram(user?.unsafeMetadata as Record<string, unknown>)) {
      navigate(
        `/completar-registro${buildMembershipFunnelQuery({ plan_tier: "free", variant_id: vid })}`,
      );
      return;
    }
    void (async () => {
      try {
        const token = await getToken();
        await fetch("/api/free-join", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ variant_id: vid }),
        });
      } catch {
        /* fire-and-forget */
      }
      navigate("/portal?from_free=true");
    })();
  }, [
    freeEnabledByVariant,
    freeDisabledByAdmin,
    fireAlert,
    freeTelegramUrl,
    variant?.id,
    isSignedIn,
    user,
    navigate,
    getToken,
  ]);

  // ── Render ──────────────────────────────────────────────────────────────────
  // AR non-super-admins are redirected to BaseTemplate ("/") via effect above.
  // Early return here (after ALL hooks) so rules of hooks are respected.
  if (shouldForceBaseTemplate) {
    return (
      <TemplateLoadingScreen data-testid="redirect-loading-gate" />
    );
  }

  // ── VPN gate para la landing exclusiva (iplocate.isAnonymous) ───────────────
  // El worker resuelve privacy iplocate para visitantes no-AR y expone
  // `liveCTA.privacy.isAnonymous` (VPN/proxy/Tor/iCloud Relay). Bloqueamos la
  // entrada a USExclusiveLanding: el contenido premium no se renderiza detrás.
  // Solo bloqueamos con señal POSITIVA (privacy != null && isAnonymous) — si
  // privacy es null (iplocate no resolvió) NO asumimos VPN.
  // Bypass: admin preview, geo_sim Clean, super admin, preview embebido.
  // El gate real se prueba con ?simulate_vpn=anonymous (sin Clean).
  const bypassVpnGate =
    forceShowConversionCtas || embeddedPreview;
  const isVpnVisitor = liveCTA.privacy?.isAnonymous === true;
  if (isUsExclusiveHost && isVpnVisitor && !bypassVpnGate) {
    return <VpnBlockedScreen lang={lang} data-testid="vpn-blocked-gate" />;
  }

  const checkoutOverlayLang = isUsExclusiveHost ? "en" : lang;
  const checkoutOverlayGateway = paymentGateway ?? "stripe";

  const conversionChromeProps = {
    labelLang,
    uiLang: lang,
    brandName: brandDisplayName,
    variantDisplayName: variant?.displayName ?? null,
    priceUsd: variant?.priceUsd ?? intlFallback.priceUsd,
    discountRate: specialOfferActive ? VIP_DISCOUNT_RATE : 0,
    paymentGateway,
  };

  // Footer del panel CTA. En mobile (overlay) NO renderizamos presentation
  // extras ni trust badges: en 360px + IG WebView apilan chips basura
  // ("Premium tier…", Exclusive/Instant) encima del Accept cookies y rompen UX.
  // Desktop (stacked) tiene aire para presentation opcional del admin.
  const buildCtaFooterSlot = (panelLayout: "overlay" | "stacked") => (
    <>
      {panelLayout === "stacked" ? (
        <DualCtaPresentationExtras presentation={variant?.presentation} />
      ) : null}
      {showVip && paymentGateway === "stripe" && (
        <div className="flex items-center justify-center mt-0.5">
          <a
            href="https://stripe.com"
            target="_blank"
            rel="noopener noreferrer"
            className="opacity-35 md:hover:opacity-55 transition-opacity"
            aria-label="Powered by Stripe"
          >
            <img
              src="https://res.cloudinary.com/nm-creators/image/upload/v1781711564/Powered_by_Stripe_-_white_csam6q.svg"
              alt="Powered by Stripe"
              className="h-[18px] w-auto"
              loading="lazy"
            />
          </a>
        </div>
      )}
      <nav
        className={cn(
          "flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-[9px] mt-1",
          "text-white/28 md:text-zinc-500",
        )}
        aria-label={t("dualCtaFooter.cookies")}
      >
        <Link to="/terminos-y-condiciones" className="md:hover:text-white/50 md:hover:text-zinc-300 transition-colors">
          {t("dualCtaFooter.terms")}
        </Link>
        <span className="text-white/15 md:text-zinc-700" aria-hidden>
          ·
        </span>
        <Link to="/privacy-policy" className="md:hover:text-white/50 md:hover:text-zinc-300 transition-colors">
          {t("dualCtaFooter.privacy")}
        </Link>
        <span className="text-white/15 md:text-zinc-700" aria-hidden>
          ·
        </span>
        <Link to="/cookie-policy" className="md:hover:text-white/50 md:hover:text-zinc-300 transition-colors">
          {t("dualCtaFooter.cookies")}
        </Link>
      </nav>
    </>
  );

  const renderMembershipCtaPanel = (panelLayout: "overlay" | "stacked") => (
    <MembershipVideoCtaPanel
      layout={panelLayout}
      language={lang}
      engaged={ctaEngaged}
      engagedNudge={localT("engagedNudge", lang)}
      processingLabel={localT("processing", lang)}
      notAvailableLabel={localT("notAvailable", lang)}
      vipLabel={effectiveVipLabel}
      vipSubtitle={effectiveVipSubtitle}
      showVip={showVip}
      showFree={showFreeButton && freeEnabledByVariant}
      freeLabel={effectiveFreeLabel}
      freeUrl={freeTelegramUrl}
      freeDisabled={freeIsGrey}
      vipDisabled={vipButtonDisabled}
      isCheckingOut={isCheckingOut}
      checkoutAvailable={checkoutAvailable}
      onVipClick={handleVipClick}
      onFreeClick={
        showFreeButton && freeEnabledByVariant ? handleFreeChannelClick : undefined
      }
      vipTestId={isUsExclusiveHost ? "us-exclusive-vip" : "ser-miembro-vip"}
      freeTestId={isUsExclusiveHost ? "us-exclusive-free" : "ser-miembro-free"}
      footerSlot={buildCtaFooterSlot(panelLayout)}
    />
  );

  return (
    <>
    <CheckoutEmailCaptureModal
      open={emailModalOpen}
      lang={lang}
      surface="DualCTAVideoLanding"
      template={hostTemplate}
      loading={isCheckingOut}
      allowSkip
      initialEmail={
        resolvePreCheckoutEmail(
          isSignedIn ? user?.primaryEmailAddress?.emailAddress : null,
        ) || ""
      }
      onSubmit={(email) => {
        void runVipCheckout(email);
      }}
      onSkip={() => {
        void runVipCheckout(null);
      }}
      onClose={() => setEmailModalOpen(false)}
    />
    {isUsExclusiveHost ? (
      <USExclusiveLandingSeoFooter countryCode={visitorCountry} />
    ) : null}
    <CheckoutRedirectOverlay
      open={isCheckingOut}
      gateway={checkoutOverlayGateway}
      language={checkoutOverlayLang}
    />
    <div
      className={cn(
        // Canvas negro + recuadro 9:16 centrado: video, CTAs, cookies y legal
        // viven DENTRO del frame (no en letterbox ni fixed fuera del video).
        embeddedPreview
          ? `absolute inset-0 ${MUX_FULLSCREEN_SHELL_CLASS} bg-black overflow-hidden flex items-center justify-center`
          : "fixed inset-0 bg-black overflow-x-hidden overflow-y-hidden flex items-center justify-center",
        embeddedSocialBrowser && "instagram-browser-modal",
      )}
      style={
        embeddedPreview
          ? {
              height: "100%",
              minHeight: "100%",
              maxHeight: "100%",
              touchAction: "none",
              overscrollBehavior: "none",
            }
          : {
              WebkitTransform: "translate3d(0,0,0)",
              transform: "translate3d(0,0,0)",
              touchAction: "none",
              overscrollBehavior: "none",
              minHeight: "100vh",
              height: "100dvh",
              maxHeight: "100dvh",
              contain: "layout paint style",
            }
      }
    >
      <div
        className={cn(
          embeddedPreview
            ? `relative flex h-full w-full min-h-0 flex-col overflow-hidden bg-black ${MUX_FULLSCREEN_FRAME_CLASS}`
            : isUsExclusiveHost
              ? "relative flex h-[100dvh] max-h-[100dvh] w-[min(100vw,calc(100dvh*9/16))] max-w-full flex-col overflow-hidden bg-black"
              : cn(
                  "relative flex h-[100dvh] max-h-[100dvh] w-full flex-col overflow-hidden bg-black",
                  "md:h-auto md:max-h-[min(100dvh,920px)] md:w-full md:max-w-[min(100vw,1080px)] md:flex-row md:items-stretch md:gap-0 md:overflow-visible md:bg-transparent md:px-4",
                ),
        )}
      >
        {/* ── Video column ──────────────────────────────────────────────── */}
        {/* instagram-browser-container aplica reglas video-cover/playsinline
            que están en applyInstagramBrowserCSS() solo cuando el visitor viene
            de Instagram/Threads/FB WebView. Sin esta clase el video podía
            arrancar maximized fuera del viewport en iOS WebView. */}
        <div
          className={cn(
            "relative bg-black overflow-hidden",
            "flex-1 min-h-0 h-full",
            embeddedPreview
              ? ""
              : cn(
                  "md:flex-none md:h-[min(100dvh,900px)] md:w-[min(calc(100dvh*9/16),min(420px,52vw))]",
                  "md:rounded-3xl md:ring-1 md:ring-white/10 md:shadow-2xl",
                ),
            embeddedSocialBrowser && "instagram-browser-container",
            "overflow-hidden",
          )}
        >

          {/* Top brand overlay — REMOVIDO per directiva user (deep fix):
              video debe estar limpio sin componentes sobre el top. El brand
              ya está implícito en el video del creator + en los botones VIP
              de abajo. brandTagline / shopAvailable / brand avatar quitados.
              Si se necesita re-agregar branding sutil, hacerlo en el footer
              del CTA panel inferior, no sobre el video. */}
          {hasVideo ? (
            <>
              {/* Poster backdrop — blurred preview while video loads */}
              <MuxPosterBackdrop
                key={`poster-${playbackId}`}
                playbackId={playbackId as string}
                fitMode="smartcrop"
                hidden={videoPlaying || videoEnded}
                onReady={() => setPosterReady(true)}
              />

              {/* Video player — absolute inset-0 para llenar la columna fullscreen.
                  containerClassName anula max-h-[90vh] del modo contain (pensado
                  para embeds en hero), así MuxVideoPlayerWithCTA ocupa el 100 %
                  del viewport detrás del panel CTA. skipWorkerTemplateCta: VIP
                  checkout vive en MembershipVideoCtaPanel + resolveIntlDualCtaFallback
                  (Stripe price/promo contract), no en el CTA interno del player.

                  Admin preview (Preview Mux country-template): hideUnmute=false so sound UI + best-effort autoplay is testable.
                  When opened from admin toolbar simulation (geo + simulate_vpn + simulate_ip via iplocate),
                  the worker provides the real geo/variant/privacy, and here we adapt layout for embedded (container scope)
                  + mobile/IG (playsinline, dvh, safe areas, instagram classes) + force sound attempts. */}
              <div 
                className="absolute inset-0" 
                data-testid="dual-cta-mux"
                onClick={() => {
                  // Best-effort: any click in the video area grants gesture and forces sound
                  // This helps the video start with sound as much as possible.
                }}
                onTouchStart={() => {
                  // Mobile gesture grant
                }}
              >
                <MuxVideoPlayerWithCTA
                  key={playbackId}
                  playbackId={playbackId as string}
                  forceManualPlaybackId={true}
                  countryCode={getVisitorCountry()}
                  displayMode="cover"
                  loop={true}
                  autoPlay="any"
                  skipWorkerTemplateCta={true}
                  viewportScope={embeddedPreview ? "container" : "page"}
                  containerClassName="!max-h-none h-full min-h-0 w-full"
                  unmuteIndicatorPosition="top-right"
                  hideUnmutePrompt={false}
                  slackFunnelTemplate={hostTemplate}
                  slackFunnelContext={{
                    videoStarted: funnelRef.current.videoStarted,
                    videoCompleted: funnelRef.current.videoCompleted,
                    ctaVipClicked: funnelRef.current.ctaVip,
                    ctaFreeClicked: funnelRef.current.ctaFree,
                    timeOnPageSeconds: Math.round(
                      (Date.now() - funnelRef.current.enteredAt) / 1000,
                    ),
                    dualVariantId: variant?.id,
                    dualVariantGeoScope: variant?.assignmentScope,
                    dualVariantGeoCode: variant?.assignmentGeo,
                  }}
                  onReady={() => setVideoReady(true)}
                  onPlay={() => {
                    setVideoNeedsGesture(false);
                    setVideoStreamFailed(false);
                    onVideoPlay();
                  }}
                  onEnded={onVideoEnded}
                  onPlaybackTelemetry={onPlaybackTelemetry}
                  onNeedsUserGesture={onVideoPlayerNeedsGesture}
                  onError={onVideoPlayerError}
                />
              </div>
            </>
          ) : (
            /* Fallback de marca cuando no hay video — los CTAs siguen visibles */
            <div
              className="absolute inset-0 bg-gradient-to-b from-zinc-900 via-black to-zinc-950"
              aria-hidden
            />
          )}

          {/* Hint cuando autoplay/red falló — el botón play del player queda accesible.
              El video intenta iniciar SIEMPRE con sonido (unmuted first + intentos post-play).
              Toca el área del video para activar audio si el browser lo bloquea.
              En mobile/IG el prompt es pequeño y no compite con los CTAs de abajo. */}
          {hasVideo && videoNeedsGesture && !videoPlaying && (
            <div
              className="pointer-events-none absolute inset-x-0 top-[max(0.5rem,env(safe-area-inset-top,8px))] z-[104] flex justify-center px-4"
              role="status"
            >
              <div className="max-w-[min(100%,18rem)] rounded-full bg-black/60 px-3 py-1.5 text-center backdrop-blur-md ring-1 ring-white/15">
                <p className="text-[10px] sm:text-[11px] font-medium text-white/90">
                  {localT("tapToPlay", lang)}
                </p>
                {videoStreamFailed && (
                  <p className="mt-0.5 text-[10px] text-white/55">
                    {localT("videoNetworkHint", lang)}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Loading indicator — stays mounted for smooth fade-out when video plays.
              Never uses a dark overlay so the MuxPosterBackdrop blur-up is
              visible from the first paint (LQIP lands in ~100ms). */}
          <div
            className={cn(
              "pointer-events-none absolute inset-0 z-[105] transition-opacity duration-700 ease-out",
              !showVideoLoadingOverlay && "opacity-0",
            )}
            role="status"
            aria-live="polite"
            aria-busy={showVideoLoadingOverlay && !videoReady}
            aria-hidden={!showVideoLoadingOverlay}
          >
            {/* Before poster is decoded: minimal centered spinner, no opaque overlay
                so the LQIP blur-up behind it gives immediate visual context. */}
            {!posterReady && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative h-9 w-9">
                  <div className="absolute inset-0 rounded-full border-2 border-white/15" />
                  <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-white/70 animate-spin" />
                </div>
              </div>
            )}
            {/* After poster decodes: small pill at the bottom while video streams. */}
            {posterReady && (
              <div
                className="absolute inset-x-0 bottom-0 flex justify-center"
                style={{
                  paddingBottom:
                    "max(5.5rem, calc(env(safe-area-inset-bottom, 0px) + 5.5rem))",
                }}
              >
                <span className="inline-flex items-center gap-2 rounded-full bg-black/45 px-3.5 py-2 backdrop-blur-md ring-1 ring-white/10">
                  <span className="relative h-3.5 w-3.5">
                    <span className="absolute inset-0 rounded-full border-2 border-white/15" />
                    <span className="absolute inset-0 rounded-full border-2 border-transparent border-t-amber-400 animate-spin" />
                  </span>
                  <span className="text-[11px] font-medium tracking-wide text-white/80">
                    {localT("loading", lang)}
                  </span>
                </span>
              </div>
            )}
          </div>

          {/* ── CTA panel — TikTok Shop: VIP membership en el pie del frame ──
              USExclusiveLanding = frame 9:16 en mobile Y desktop (letterbox).
              Overlay CTA SIEMPRE (sin md:hidden): mobile real, desktop real,
              geo-sim Clean. Sin VIP la landing es dead-end.

              DualCTA /ser-miembro (no exclusive): overlay mobile + stacked md+. */}
          {isUsExclusiveHost ? (
            <div
              className="contents"
              data-testid="us-exclusive-cta-slot"
              data-show-vip={showVip ? "1" : "0"}
            >
              {renderMembershipCtaPanel("overlay")}
            </div>
          ) : (
            <div className="md:hidden">{renderMembershipCtaPanel("overlay")}</div>
          )}
        </div>
        {/* ── end video column ── */}

        {!embeddedPreview && !isUsExclusiveHost ? (
          <div className="hidden md:flex md:flex-col md:justify-center md:gap-6 md:py-8 md:pl-6 md:pr-2 md:flex-1 md:min-w-0">
            <MembershipFunnelDesktopAside {...conversionChromeProps} />
            {renderMembershipCtaPanel("stacked")}
          </div>
        ) : null}
      </div>
      {/* ── end centered video frame ── */}
    </div>
    </>
  );
}
